import { useState, useRef } from 'react';
import { Music, Upload, X, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { MIX_FEEDBACK_TAGS } from './index';

export const MixUploadModal = ({ isOpen, onClose, onUpload, uploadLimits }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > 150 * 1024 * 1024) {
        alert('File size must be less than 150MB');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!title || !description || selectedTags.length === 0 || !file) return;
    
    setUploading(true);
    try {
      await onUpload({ title, description, tags: selectedTags, file });
      setTitle('');
      setDescription('');
      setSelectedTags([]);
      setFile(null);
      onClose();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const remainingUploads = uploadLimits 
    ? uploadLimits.uploads_per_week - uploadLimits.uploads_this_week 
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div 
        className="rounded-2xl p-6 border max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#1a1a1a', border: '1px solid rgba(212, 175, 55, 0.3)' }}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Music size={24} style={{ color: '#D4AF37' }} />
            Upload Mix
          </h3>
          <button onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {uploadLimits && remainingUploads <= 0 ? (
          <div className="text-center py-8">
            <AlertTriangle size={48} className="mx-auto mb-4 text-yellow-500" />
            <h4 className="text-lg font-bold mb-2">Upload Limit Reached</h4>
            <p className="text-gray-400">
              You've used all {uploadLimits.uploads_per_week} uploads for this week.
              {uploadLimits.tier !== 'elite' && ' Upgrade to Elite for unlimited uploads!'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {uploadLimits && (
              <div 
                className="p-3 rounded-lg text-sm"
                style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.2)' }}
              >
                <p style={{ color: '#D4AF37' }}>
                  {remainingUploads} upload{remainingUploads !== 1 ? 's' : ''} remaining this week
                  {uploadLimits.max_duration_minutes && ` | Max ${uploadLimits.max_duration_minutes} min`}
                </p>
              </div>
            )}
            
            <div>
              <Label>Mix Title *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Summer House Mix 2024"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description *</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Tell us about your mix..."
                rows={3}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Feedback Categories * (select at least one)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {MIX_FEEDBACK_TAGS.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                      selectedTags.includes(tag.id)
                        ? 'bg-[#D4AF37]/20 border-[#D4AF37]'
                        : 'border-gray-600 hover:border-[#D4AF37]/50'
                    }`}
                    style={{ color: selectedTags.includes(tag.id) ? '#D4AF37' : '#a3a3a3' }}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Audio File * (MP3, WAV, AIFF - Max 150MB)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".mp3,.wav,.aiff,audio/mpeg,audio/wav,audio/aiff"
                onChange={handleFileSelect}
                className="hidden"
              />
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="mt-2 p-6 rounded-xl border-2 border-dashed cursor-pointer text-center transition-colors hover:border-[#D4AF37]/50"
                style={{ borderColor: file ? '#D4AF37' : '#333' }}
              >
                {file ? (
                  <div>
                    <Music size={32} className="mx-auto mb-2" style={{ color: '#D4AF37' }} />
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                ) : (
                  <div>
                    <Upload size={32} className="mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-400">Click to select audio file</p>
                  </div>
                )}
              </div>
            </div>
            
            <Button 
              onClick={handleUpload}
              disabled={uploading || !title || !description || selectedTags.length === 0 || !file}
              className="w-full rounded-full"
              style={{ background: '#D4AF37', color: '#000' }}
            >
              {uploading ? 'Uploading...' : 'Upload Mix'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
